import type { GeometryDataTyped } from "@/types/blocks.types";
import {
  ParametricAssetId,
  SubmittingOrganization,
  type ParametricAsset,
} from "@/types";

type AssetParams = {
  isOneHole: boolean;
  a: number; // Hauteur sous la buse
  b: number; // Diamètre buse
  height: number; // Hauteur total
  c: number; // Écart entre les conduits
  d: number; // Largeur bloc milieu
  e: number; // Largeur haut gauche
  f: number; // Largeur haut droite
  g: number; // Hauteur gauche
  h: number; // Hauteur droite
  i: number; // Largeur bas gauche
  j: number; // Largeur bas droite
};

const keysList: (keyof AssetParams)[] = [
  "isOneHole",
  "a",
  "b",
  "height",
  "c",
  "d",
  "e",
  "f",
  "g",
  "h",
  "i",
  "j",
];

const asset: ParametricAsset<AssetParams> = {
  id: ParametricAssetId.CULVERT_HEADWALL,
  image: "/images/assets/tete-buse-standart.png",
  imageToShow: (data) =>
    data.isOneHole
      ? "/images/assets/tete-buse-standart.png"
      : "/images/assets/tete-buse-standart-1.png",
  submittedBy: SubmittingOrganization.GETEC,
  inputs: [
    {
      key: "isOneHole",
      type: "boolean",
    },
    {
      key: "a",
      type: "dimension",
    },
    {
      key: "b",
      type: "dimension",
    },
    {
      key: "height",
      type: "dimension",
    },
    {
      key: "c",
      type: "dimension",
    },
    {
      key: "d",
      type: "dimension",
    },
    {
      key: "e",
      type: "dimension",
    },
    {
      key: "f",
      type: "dimension",
    },
    {
      key: "g",
      type: "dimension",
    },
    {
      key: "h",
      type: "dimension",
    },
    {
      key: "i",
      type: "dimension",
    },
    {
      key: "j",
      type: "dimension",
    },
  ],
  inputsToShow: (data) => {
    return data.isOneHole ? keysList.filter((key) => key !== "c") : keysList;
  },
  build: (params, offset) => {
    const { a, b, c, d, e, f, g, h, i, j, isOneHole, height } = params;

    const res: GeometryDataTyped[] = [
      {
        type: "polyline",
        points: [
          [offset[0], offset[1], offset[2]],
          [offset[0] + i, offset[1], offset[2]],
          [offset[0] + i, offset[1] + height, offset[2]],
          [offset[0] + i - e, offset[1] + height, offset[2]],
          [offset[0], offset[1] + g, offset[2]],
          [offset[0], offset[1], offset[2]],
        ],
      },
      {
        type: "polyline",
        points: [
          [offset[0] + i, offset[1], offset[2]],
          [offset[0] + i + d, offset[1], offset[2]],
          [offset[0] + i + d, offset[1] + height, offset[2]],
          [offset[0] + i, offset[1] + height, offset[2]],
          [offset[0] + i, offset[1], offset[2]],
        ],
      },
      {
        type: "polyline",
        points: [
          [offset[0] + i + d, offset[1], offset[2]],
          [offset[0] + i + d + j, offset[1], offset[2]],
          [offset[0] + i + d + j, offset[1] + h, offset[2]],
          [offset[0] + i + d + f, offset[1] + height, offset[2]],
          [offset[0] + i + d, offset[1] + height, offset[2]],
          [offset[0] + i + d, offset[1], offset[2]],
        ],
      },
    ];

    if (isOneHole) {
      res.push({
        type: "ellipse",
        center: [offset[0] + i + d / 2, offset[1] + a + b / 2, offset[2]],
        minorPoint: [
          offset[0] + i + d / 2 + b / 2,
          offset[1] + a + b / 2,
          offset[2],
        ],
        majorPoint: [offset[0] + i + d / 2, offset[1] + a + b, offset[2]],
      });
    } else {
      res.push(
        {
          type: "ellipse",
          center: [
            offset[0] + i + (d - c - b) / 2,
            offset[1] + a + b / 2,
            offset[2],
          ],
          minorPoint: [
            offset[0] + i + (d - c) / 2,
            offset[1] + a + b / 2,
            offset[2],
          ],
          majorPoint: [
            offset[0] + i + (d - c - b) / 2,
            offset[1] + a + b,
            offset[2],
          ],
        },
        {
          type: "ellipse",
          center: [
            offset[0] + i + (d + c + b) / 2,
            offset[1] + a + b / 2,
            offset[2],
          ],
          minorPoint: [
            offset[0] + i + (d + c) / 2 + b,
            offset[1] + a + b / 2,
            offset[2],
          ],
          majorPoint: [
            offset[0] + i + (d + c + b) / 2,
            offset[1] + a + b,
            offset[2],
          ],
        }
      );
    }

    return res;
  },
};
export default asset;
